import { type CSSProperties, type JSX, type ReactNode, type Ref } from 'react';
import {
  DefaultNodeOptions,
  isJsonError,
  isParentNode,
  JsonError,
  Node,
} from '@sagold/react-json-editor';
import clsx from 'clsx';

import { FormDescription } from './FormDescription';
import { FormError } from './FormError';

export type FormFieldProps = {
  widgetType: string;
  node: Node;
  /** widget options passed through json-editor widget decorator */
  options: DefaultNodeOptions<{ showErrors?: boolean }>;
  showError?: boolean;
  additionalError?: JsonError;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /* if set, errors will be taken from this list instead of node.errors.
        Example: Display all errors of children in this widget */
  errors?: JsonError[];
  tableLayout?: boolean;
  testId?: string;
  ref?: Ref<HTMLDivElement>;
};

type FormFieldComponent = ((props: FormFieldProps) => JSX.Element) & {
  Header: typeof FormFieldHeader;
  Error: typeof FormError;
  Description: typeof FormDescription;
};

/**
 * container for root of a widget
 */
export const FormField = function FormField({
  widgetType,
  node,
  options,
  children,
  errors,
  additionalError,
  showError,
  className,
  style,
  tableLayout,
  testId,
  ref,
}: FormFieldProps) {
  const listOfErrors = Array.isArray(errors) ? errors : node.errors;
  const hasError =
    showError !== false &&
    options.showErrors !== false &&
    (node.errors.length > 0 || isJsonError(additionalError));

  const parentNode = isParentNode(node);
  const schemaType = node.schema.type;
  const { disabled = false } = options;
  return (
    <div
      className={clsx(
        'rje-field',
        `rje-field--${parentNode ? 'parent' : 'value'}`,
        `rje-field--${schemaType}`,
        `rje-field--${widgetType}`,
        disabled ? 'rje-field--disabled' : 'rje-field--enabled',
        hasError ? ' rje-field--error' : 'rje-field--valid',
        className,
        options.classNames
      )}
      data-type={schemaType}
      data-id={node.pointer}
      data-testid={testId}
      style={style}
      ref={ref}
    >
      {children}
      {hasError && (
        <FormError
          tableLayout={tableLayout}
          errors={additionalError ? [additionalError] : listOfErrors}
        />
      )}
    </div>
  );
} as FormFieldComponent;

FormField.Header = FormFieldHeader;
FormField.Error = FormError;
FormField.Description = FormDescription;

export type FormFieldHeaderProps = {
  children?: ReactNode | ReactNode[] | null;
};

function FormFieldHeader({ children }: FormFieldHeaderProps) {
  if (children == null) {
    return null;
  }
  return <div className="rje-field__header">{children}</div>;
}
